import React, { useState } from 'react';
import { X, FileText, Globe, CheckCircle2 } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { API_BASE as API } from '../../lib/apiBase';

const COUNTRIES = [
  { code: 'ES', name: 'Espana' }, { code: 'US', name: 'Estados Unidos' },
  { code: 'MX', name: 'Mexico' }, { code: 'CO', name: 'Colombia' },
  { code: 'AR', name: 'Argentina' }, { code: 'CL', name: 'Chile' },
  { code: 'PE', name: 'Peru' }, { code: 'GB', name: 'Reino Unido' },
  { code: 'DE', name: 'Alemania' }, { code: 'FR', name: 'Francia' },
  { code: 'BR', name: 'Brasil' }, { code: 'IT', name: 'Italia' },
  { code: 'OTHER', name: 'Otro' },
];

export default function PublishPanel({ visible, onClose, sessionId, project, token, user, onPublished }) {
  const { lang } = useLang();
  const [step, setStep] = useState(1); // 1=internal, 2=external (optional)
  const [country, setCountry] = useState('ES');
  const [songTitle, setSongTitle] = useState(project?.base?.title || '');
  const [artistName, setArtistName] = useState(user?.name || '');
  const [acceptInternal, setAcceptInternal] = useState(false);
  const [acceptOriginality, setAcceptOriginality] = useState(false);
  const [acceptExternal, setAcceptExternal] = useState(false);
  const [acceptRevenue, setAcceptRevenue] = useState(false);
  const [acceptCredit, setAcceptCredit] = useState(false);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);

  if (!visible) return null;

  const canSignInternal = acceptInternal && acceptOriginality && songTitle.trim() && artistName.trim();
  const canSignExternal = acceptExternal && acceptRevenue && acceptCredit;

  const handleSign = async () => {
    setSigning(true);
    try {
      const res = await fetch(`${API}/api/producer/publish-agreement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionId,
          song_title: songTitle,
          artist_name: artistName,
          country,
          accept_internal: acceptInternal,
          accept_external: step === 2 ? acceptExternal : false,
          accept_credit_ft_pulse: step === 2 ? acceptCredit : false,
          accept_revenue_share_30: step === 2 ? acceptRevenue : false,
          accept_originality: acceptOriginality,
        }),
      });
      if (res.ok) {
        setDone(true);
        onPublished?.();
      }
    } catch (e) {
      console.error('Publish error:', e);
    } finally {
      setSigning(false);
    }
  };

  const es = lang === 'es';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="publish-panel">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-[520px] max-w-[95vw] max-h-[85vh] overflow-y-auto rounded-2xl border border-[#27272A] bg-[#0A0A0D] shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#0A0A0D] border-b border-[#1A1A1F] p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-[#00D4FF]" />
            <h2 className="text-sm font-bold text-white">
              {es ? 'Publicar en PulseMusic' : 'Publish on PulseMusic'}
            </h2>
          </div>
          <button onClick={onClose} className="w-6 h-6 rounded-full bg-[#1A1A1F] flex items-center justify-center text-[#52525B] hover:text-white">
            <X size={12} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 size={48} className="text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2">
              {es ? 'Publicado' : 'Published'}
            </h3>
            <p className="text-sm text-[#A1A1AA]">
              {es ? `"${songTitle}" por ${artistName} ft PULSE ya esta en PulseMusic.` : `"${songTitle}" by ${artistName} ft PULSE is now on PulseMusic.`}
            </p>
            <button onClick={onClose} className="mt-6 px-6 py-2 rounded-lg text-sm font-bold text-black" style={{ background: '#00D4FF' }}>
              {es ? 'Cerrar' : 'Close'}
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Song details */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[#71717A] uppercase tracking-wider block mb-1">
                  {es ? 'Titulo' : 'Title'}
                </label>
                <input
                  data-testid="publish-title"
                  value={songTitle}
                  onChange={e => setSongTitle(e.target.value)}
                  className="w-full bg-[#111114] border border-[#27272A] rounded-lg px-3 py-2 text-xs text-white focus:border-[#00D4FF40] outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#71717A] uppercase tracking-wider block mb-1">
                  {es ? 'Artista' : 'Artist'}
                </label>
                <input
                  data-testid="publish-artist"
                  value={artistName}
                  onChange={e => setArtistName(e.target.value)}
                  className="w-full bg-[#111114] border border-[#27272A] rounded-lg px-3 py-2 text-xs text-white focus:border-[#00D4FF40] outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-[#71717A] uppercase tracking-wider block mb-1">
                {es ? 'Pais de residencia' : 'Country of residence'}
              </label>
              <select
                data-testid="publish-country"
                value={country}
                onChange={e => setCountry(e.target.value)}
                className="w-full bg-[#111114] border border-[#27272A] rounded-lg px-3 py-2 text-xs text-white outline-none"
              >
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>

            {/* Credit preview */}
            <div className="bg-[#111114] border border-[#27272A] rounded-xl p-3 text-center">
              <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-1">
                {es ? 'Se publicara como' : 'Will be published as'}
              </p>
              <p className="text-sm font-bold text-white">{artistName || '...'} <span className="text-[#00D4FF]">ft PULSE</span></p>
              <p className="text-xs text-[#71717A] mt-0.5">{songTitle || '...'}</p>
            </div>

            {/* Step 1: Internal publication */}
            {step === 1 && (
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-white flex items-center gap-1.5">
                  <Globe size={12} className="text-[#00D4FF]" />
                  {es ? 'Publicacion en PulseMusic' : 'Publication on PulseMusic'}
                </h3>

                <div className="bg-[#111114] rounded-xl p-3 text-[10px] text-[#A1A1AA] leading-relaxed space-y-2 max-h-[180px] overflow-y-auto producer-scroll">
                  <p className="font-semibold text-white text-[11px]">
                    {es ? 'Acuerdo de Publicacion — Resumen' : 'Publication Agreement — Summary'}
                  </p>
                  {es ? (
                    <>
                      <p>Tu obra sigue siendo tu proyecto artistico. Cuando la publicas en PulseMusic, nos concedes unicamente los permisos necesarios para alojarla, promocionarla y monetizarla dentro de la plataforma.</p>
                      <p>La monetizacion interna funciona en las mismas condiciones que cualquier artista de la plataforma. PulseMusic no cobra comision adicional sobre tus ingresos internos.</p>
                      <p>El credito de publicacion incluira la mencion "ft PULSE" como reconocimiento de la co-produccion asistida.</p>
                      <p>Podras retirar tu obra de PulseMusic en cualquier momento, con efecto en un plazo de 14 dias naturales.</p>
                    </>
                  ) : (
                    <>
                      <p>Your work remains your artistic project. By publishing on PulseMusic, you grant us only the permissions needed to host, promote, and monetize it within the platform.</p>
                      <p>Internal monetization works under the same conditions as any platform artist. PulseMusic does not charge additional commission on your internal earnings.</p>
                      <p>The publication credit will include "ft PULSE" as recognition of the assisted co-production.</p>
                      <p>You may remove your work from PulseMusic at any time, effective within 14 calendar days.</p>
                    </>
                  )}
                </div>

                {/* Checkboxes */}
                <label className="flex items-start gap-2 cursor-pointer group">
                  <input type="checkbox" checked={acceptInternal} onChange={e => setAcceptInternal(e.target.checked)} className="mt-0.5 accent-[#00D4FF]" />
                  <span className="text-[10px] text-[#A1A1AA] group-hover:text-white transition-colors">
                    {es
                      ? 'He leido y acepto el Acuerdo de Publicacion en PulseMusic.'
                      : 'I have read and accept the PulseMusic Publication Agreement.'}
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer group">
                  <input type="checkbox" checked={acceptOriginality} onChange={e => setAcceptOriginality(e.target.checked)} className="mt-0.5 accent-[#00D4FF]" />
                  <span className="text-[10px] text-[#A1A1AA] group-hover:text-white transition-colors">
                    {es
                      ? 'Confirmo que dispongo de los derechos necesarios sobre los elementos que he aportado a la obra.'
                      : 'I confirm I hold the necessary rights to the elements I contributed to the work.'}
                  </span>
                </label>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSign}
                    disabled={!canSignInternal || signing}
                    className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
                    style={{ background: canSignInternal ? '#00D4FF' : '#27272A', color: canSignInternal ? '#000' : '#52525B' }}
                    data-testid="sign-internal-btn"
                  >
                    {signing ? (es ? 'Firmando...' : 'Signing...') : (es ? 'Firmar y publicar' : 'Sign and publish')}
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    className="px-4 py-2.5 rounded-lg text-xs font-medium border border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#52525B] transition-colors"
                  >
                    {es ? 'Tambien exportar fuera' : 'Also export externally'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: External distribution */}
            {step === 2 && (
              <div className="space-y-3">
                <h3 className="text-xs font-bold text-white flex items-center gap-1.5">
                  <Globe size={12} className="text-[#F5C842]" />
                  {es ? 'Distribucion externa' : 'External distribution'}
                </h3>

                <div className="bg-[#111114] rounded-xl p-3 text-[10px] text-[#A1A1AA] leading-relaxed space-y-2 max-h-[180px] overflow-y-auto producer-scroll">
                  <p className="font-semibold text-white text-[11px]">
                    {es ? 'Participacion en ingresos — Distribucion externa' : 'Revenue sharing — External distribution'}
                  </p>
                  {es ? (
                    <>
                      <p>Si decides exportar, licenciar, distribuir o explotar comercialmente la Obra fuera del ecosistema PulseMusic mediante una version creada, generada, masterizada, arreglada o asistida de forma sustancial por PULSE, aceptas expresamente que dicha explotacion externa quedara sujeta a una participacion contractual en ingresos netos del <strong className="text-white">30% a favor de PulseMusic</strong> y del <strong className="text-white">70% a favor del Artista</strong>.</p>
                      <p>A efectos del presente acuerdo, esa participacion del 30% remunera: (i) la co-produccion tecnologica y creativa asistida por PULSE; y (ii) la infraestructura, procesamiento, herramientas de produccion y servicios de plataforma puestos a disposicion del Usuario.</p>
                      <p>Esta participacion sera exigible mientras la Obra, o cualquier version sustancialmente derivada de ella, permanezca explotada comercialmente fuera de PulseMusic.</p>
                      <p><strong className="text-white">Creditos de co-produccion:</strong> En toda distribucion externa, deberas mantener una mencion razonable y proporcionada a la participacion de PULSE, utilizando, cuando el canal lo permita, una formula equivalente a: "{artistName || 'Artista'} ft. PULSE", "Produced with PULSE" o "Co-produced with PULSE Producer by PulseMusic". Cuando una plataforma o distribuidor no permita ese formato exacto en el titulo principal, deberas incluirlo en los creditos, metadatos descriptivos o materiales promocionales asociados al lanzamiento.</p>
                    </>
                  ) : (
                    <>
                      <p>If you choose to export, license, distribute, or commercially exploit the Work outside the PulseMusic ecosystem using a version created, generated, mastered, arranged, or substantially assisted by PULSE, you expressly agree that such external exploitation shall be subject to a contractual revenue share of <strong className="text-white">30% in favor of PulseMusic</strong> and <strong className="text-white">70% in favor of the Artist</strong>.</p>
                      <p>For the purposes of this agreement, the 30% share compensates: (i) the technological and creative co-production assisted by PULSE; and (ii) the infrastructure, processing, production tools, and platform services made available to the User.</p>
                      <p>This share shall be enforceable for as long as the Work, or any substantially derived version thereof, remains commercially exploited outside PulseMusic.</p>
                      <p><strong className="text-white">Co-production credits:</strong> In all external distribution, you shall maintain a reasonable and proportionate mention of PULSE's participation, using, where the channel permits, a formula equivalent to: "{artistName || 'Artist'} ft. PULSE", "Produced with PULSE", or "Co-produced with PULSE Producer by PulseMusic".</p>
                    </>
                  )}
                </div>

                <label className="flex items-start gap-2 cursor-pointer group">
                  <input type="checkbox" checked={acceptExternal} onChange={e => setAcceptExternal(e.target.checked)} className="mt-0.5 accent-[#F5C842]" />
                  <span className="text-[10px] text-[#A1A1AA] group-hover:text-white transition-colors">
                    {es
                      ? 'Acepto que, si exploto esta obra fuera de PulseMusic, se aplicara a PulseMusic una participacion contractual del 30% sobre los ingresos netos externos.'
                      : 'I accept that if I exploit this work outside PulseMusic, a 30% contractual share on net external revenues will apply to PulseMusic.'}
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer group">
                  <input type="checkbox" checked={acceptRevenue} onChange={e => setAcceptRevenue(e.target.checked)} className="mt-0.5 accent-[#F5C842]" />
                  <span className="text-[10px] text-[#A1A1AA] group-hover:text-white transition-colors">
                    {es
                      ? 'Entiendo la division 70/30 (Artista/PulseMusic) y acepto su aplicacion.'
                      : 'I understand the 70/30 split (Artist/PulseMusic) and accept its application.'}
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer group">
                  <input type="checkbox" checked={acceptCredit} onChange={e => setAcceptCredit(e.target.checked)} className="mt-0.5 accent-[#F5C842]" />
                  <span className="text-[10px] text-[#A1A1AA] group-hover:text-white transition-colors">
                    {es
                      ? 'Acepto mantener el credito de co-produccion con PULSE cuando el canal de distribucion lo permita razonablemente.'
                      : 'I agree to maintain the PULSE co-production credit when the distribution channel reasonably permits.'}
                  </span>
                </label>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setStep(1)}
                    className="px-4 py-2.5 rounded-lg text-xs text-[#52525B] hover:text-white transition-colors"
                  >
                    {es ? 'Volver' : 'Back'}
                  </button>
                  <button
                    onClick={handleSign}
                    disabled={!canSignInternal || !canSignExternal || signing}
                    className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
                    style={{ background: canSignInternal && canSignExternal ? 'linear-gradient(135deg, #F5C842, #FF6B00)' : '#27272A', color: canSignInternal && canSignExternal ? '#000' : '#52525B' }}
                    data-testid="sign-external-btn"
                  >
                    {signing ? (es ? 'Firmando...' : 'Signing...') : (es ? 'Firmar publicacion + exportacion' : 'Sign publication + export')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
